/**
 * 급여(역할 salary) 공제 계산. Cloud Functions의 functions/src/salaryTax.ts와 동일한 로직 유지.
 */

/** 세율: 총 급여 대비 %, 고정: 포인트 절대값(순서대로 공제, 잔액 한도) */
export type SalaryTaxLineMode = 'rate' | 'fixed';

export interface SalaryTaxLineConfig {
  id: string;
  label: string;
  mode: SalaryTaxLineMode;
  /** mode === 'rate' 일 때만 사용 */
  ratePercent: number;
  /** mode === 'fixed' 일 때만 사용 */
  fixedAmount: number;
}

export interface SalaryTaxSettings {
  enabled: boolean;
  lines: SalaryTaxLineConfig[];
}

export interface SalaryTaxLineResult {
  label: string;
  amount: number;
}

export interface SalaryDeductionResult {
  gross: number;
  taxLines: SalaryTaxLineResult[];
  totalTax: number;
  net: number;
}

export const DEFAULT_SALARY_TAX_SETTINGS: SalaryTaxSettings = {
  enabled: false,
  lines: [],
};

export function computeSalaryDeduction(
  gross: number,
  settings: SalaryTaxSettings | null | undefined
): SalaryDeductionResult {
  const g = Math.max(0, Math.floor(Number(gross) || 0));
  if (!settings || !settings.enabled || !settings.lines?.length) {
    return {
      gross: g,
      taxLines: [],
      totalTax: 0,
      net: g,
    };
  }
  // 항목 순서대로 공제. 세율은 항상 총 급여(g) 기준, 고정은 남은 잔액 한도.
  let remaining = g;
  const taxLines: SalaryTaxLineResult[] = [];
  for (const line of settings.lines) {
    const mode: SalaryTaxLineMode = line.mode === 'fixed' ? 'fixed' : 'rate';
    let amount = 0;
    if (mode === 'fixed') {
      const fx = Math.max(0, Math.floor(Number(line.fixedAmount) || 0));
      amount = Math.min(remaining, fx);
    } else {
      const rate = Math.max(0, Number(line.ratePercent) || 0);
      amount = Math.floor((g * rate) / 100);
      amount = Math.min(amount, remaining);
    }
    taxLines.push({ label: line.label?.trim() || '공제', amount });
    remaining -= amount;
  }
  const totalTax = g - remaining;
  const net = Math.max(0, remaining);
  return { gross: g, taxLines, totalTax, net };
}

/** Firestore salaryTaxSettings 문서 → SalaryTaxSettings */
export function parseSalaryTaxSettings(data: Record<string, unknown> | undefined): SalaryTaxSettings {
  if (!data) return { ...DEFAULT_SALARY_TAX_SETTINGS };
  const linesRaw = data.lines;
  const lines: SalaryTaxLineConfig[] = Array.isArray(linesRaw)
    ? linesRaw
        .map((row: unknown) => {
          const r = row as Record<string, unknown>;
          const legacyRate = Number(r.ratePercent) || 0;
          const mode: SalaryTaxLineMode =
            r.mode === 'fixed' ? 'fixed' : r.mode === 'rate' ? 'rate' : legacyRate > 0 ? 'rate' : 'fixed';
          return {
            id: String(r.id ?? ''),
            label: String(r.label ?? ''),
            mode,
            ratePercent: legacyRate,
            fixedAmount: Number(r.fixedAmount) || 0,
          };
        })
        .filter((l) => l.id || l.label)
    : [];
  return {
    enabled: data.enabled === true,
    lines,
  };
}

/** pointHistories 문서에 넣을 급여 관련 필드(세금 스냅샷) */
export function buildSalaryPointHistoryFields(
  studentUid: string,
  deduction: SalaryDeductionResult,
  reason: string,
  periodType: 'daily' | 'weekly' | 'monthly'
) {
  return {
    uid: studentUid,
    studentId: studentUid,
    amount: deduction.net,
    grossAmount: deduction.gross,
    taxLines: deduction.taxLines,
    totalTax: deduction.totalTax,
    kind: 'salary' as const,
    reason,
    type: periodType,
  };
}
